import { useEffect, useState } from "react";
import { Spinner, useToast } from "@chakra-ui/react";
import {
  CreateUpdateSignatureInput,
  Signature,
  useCreateSignatureMutation,
  useGetSignatureLazyQuery,
  useUpdateSignatureMutation,
} from "../../generated/graphql";
import { useErrorMessage } from "../../hooks/errorMessage";
import {
  FormMutationType,
  InheritFullWidthModalProps,
} from "../../components/FullWidthModal/interface";
import FullWidthModal from "../../components/FullWidthModal";
import MCFormSignature from "../../components/MCFormSignature";

const SignatureForm: React.FC<InheritFullWidthModalProps<Signature>> = ({
  id,
  onClose,
  isOpen,
  ...props
}) => {
  const [getSignature, { data, loading: isLoadingData }] =
    useGetSignatureLazyQuery();

  useEffect(() => {
    if (data) {
      const { name, image, enable } = data?.getSignature as Signature;

      setSignatureValue({
        name,
        image,
        enable,
      });

      setImageUrl(data.getSignature.imageUrl);
    }
  }, [data]);

  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const toast = useToast();

  const { showErrorMessage } = useErrorMessage();

  const [signatureValue, setSignatureValue] =
    useState<CreateUpdateSignatureInput>();
  const [createSignatureMutation] = useCreateSignatureMutation();
  const [updateSignatureMutation] = useUpdateSignatureMutation();

  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (id && isOpen) {
      getSignature({ variables: { id } });
    }
  }, [id, isOpen, getSignature]);

  const reset = () => {
    setSignatureValue(undefined);
    setImageUrl("");
  };

  const saveSignature = async (input: CreateUpdateSignatureInput) => {
    try {
      setIsLoadingSave(true);

      if (id) {
        const res = await updateSignatureMutation({
          variables: {
            id,
            input,
          },
        });
        toast({
          description: "Signature successfully updated!",
          status: "success",
        });
        onClose({
          type: FormMutationType.UPDATE,
          data: res.data?.updateSignature as Signature,
        });
      } else {
        await createSignatureMutation({
          variables: {
            input,
          },
        });
        toast({
          description: "Signature successfully added!",
          status: "success",
        });
        onClose({ type: FormMutationType.CREATE });
      }

      reset();
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsLoadingSave(false);
    }
  };

  return (
    <FullWidthModal
      title={id ? "Edit Signature" : "New Signature"}
      onClose={() => {
        reset();
        onClose();
      }}
      isOpen={isOpen}
      {...props}
    >
      {isLoadingData ? (
        <Spinner />
      ) : (
        <MCFormSignature
          value={signatureValue}
          imageUrl={imageUrl || ""}
          onBack={() => {
            onClose();
          }}
          onSubmit={(value) => {
            saveSignature(value);
          }}
          isLoadingSave={isLoadingSave}
        />
      )}
    </FullWidthModal>
  );
};

export default SignatureForm;
